import { useState, useEffect } from "react";
import { GeneratePDFService } from "../services/GeneratePDFService";

/**
 * useFinancialSummary
 *
 * A job can contain BOTH Closet forms and Kitchen forms. Each type has its
 * own payment schedule:
 *   - Closet:  user picks a deposit percentage (25% / 50% / Custom);
 *              balance due is whatever is left.
 *   - Kitchen: fixed 50/40/10 installment schedule, no manual deposit pick.
 *
 * So financial info is tracked as two independent blocks — `closet` and
 * `kitchen` — each with its own subtotal/HST/total/deposit breakdown.
 * Which block(s) the caller renders/saves is controlled by
 * `hasClosetForms` / `hasKitchenForms`, derived from the job's actual
 * child forms (see computeFormTypeFlags in Home.js).
 */
function useFinanceBlock() {
  const [subtotal, setSubtotal] = useState("");
  const [hst, setHst] = useState("");
  const [total, setTotal] = useState("");
  const [deposit, setDeposit] = useState("");
  const [progressPayment, setProgressPayment] = useState(""); // kitchen only
  const [balanceDue, setBalanceDue] = useState("");
  const [lastEdited, setLastEdited] = useState("subtotal");
  const [depositOption, setDepositOption] = useState(""); // closet only
  const [paymentMethod, setPaymentMethod] = useState("");
  const [chequeNumber, setChequeNumber] = useState("");

  return {
    subtotal, setSubtotal,
    hst, setHst,
    total, setTotal,
    deposit, setDeposit,
    progressPayment, setProgressPayment,
    balanceDue, setBalanceDue,
    lastEdited, setLastEdited,
    depositOption, setDepositOption,
    paymentMethod, setPaymentMethod,
    chequeNumber, setChequeNumber,
  };
}

export function useFinancialSummary({
  jobID,
  parentClosetsForm,
  designFileURL,
  pricePresentationFileURL,
  notify,
  hasClosetForms,
  hasKitchenForms,
}) {
  const closet = useFinanceBlock();
  const kitchen = useFinanceBlock();

  const [statusCard, setStatusCard] = useState(null);
  const [statusCardNumber, setStatusCardNumber] = useState("");
  const [paymentDetailsAdded, setPaymentDetailsAdded] = useState(false);

  // ── HST calc — same logic, run independently for each block ──
  const useHstCalc = (block) => {
    useEffect(() => {
      const parsedSubtotal = parseFloat(block.subtotal) || 0;
      const parsedTotal = parseFloat(block.total) || 0;
      const hstPercentage = statusCard === true ? 0.05 : 0.13;

      if (block.lastEdited === "subtotal") {
        const calculatedHst = +(parsedSubtotal * hstPercentage).toFixed(2);
        const calculatedTotal = +(parsedSubtotal + calculatedHst).toFixed(2);
        block.setHst(calculatedHst.toString());
        block.setTotal(calculatedTotal.toString());
      } else if (block.lastEdited === "total") {
        const estimatedSubtotal = +(parsedTotal / (1 + hstPercentage)).toFixed(2);
        const estimatedHst = +(parsedTotal - estimatedSubtotal).toFixed(2);
        block.setSubtotal(estimatedSubtotal.toString());
        block.setHst(estimatedHst.toString());
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [block.subtotal, block.total, block.lastEdited, statusCard]);
  };
  useHstCalc(closet);
  useHstCalc(kitchen);

  // ── KITCHEN: fixed 50-40-10 payment schedule ──
  useEffect(() => {
    const totalNum = parseFloat(kitchen.total);
    if (!isNaN(totalNum) && totalNum > 0) {
      const installment1 = +(totalNum * 0.5).toFixed(2);
      const installment2 = +(totalNum * 0.4).toFixed(2);
      const installment3 = +(totalNum - installment1 - installment2).toFixed(2);
      kitchen.setDeposit(installment1.toFixed(2));
      kitchen.setProgressPayment(installment2.toFixed(2));
      kitchen.setBalanceDue(installment3.toFixed(2));
    } else {
      kitchen.setDeposit("");
      kitchen.setProgressPayment("");
      kitchen.setBalanceDue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitchen.total]);

  // ── CLOSET: deposit percentage chosen by the user ──
  useEffect(() => {
    if (closet.depositOption === "25%") {
      closet.setDeposit(((25 / 100) * closet.total).toFixed(2));
    } else if (closet.depositOption === "50%") {
      closet.setDeposit(((50 / 100) * closet.total).toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closet.depositOption, closet.total]);

  // ── CLOSET: balance due ──
  useEffect(() => {
    const depositNum = parseFloat(closet.deposit);
    const totalNum = parseFloat(closet.total);
    if (!isNaN(depositNum) && !isNaN(totalNum)) {
      const remaining = totalNum - depositNum;
      closet.setBalanceDue(remaining >= 0 ? remaining.toFixed(2) : "");
    } else {
      closet.setBalanceDue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closet.deposit, closet.total]);

  const handlePaymentDetails = async () => {
    try {
      if (!hasClosetForms && !hasKitchenForms) {
        notify("This job has no closet or kitchen forms to bill.", "warning");
        return;
      }
      if (hasClosetForms && (closet.total < 1 || !closet.total)) {
        notify("Please enter a valid Closet total amount!", "warning");
        return;
      }
      if (hasKitchenForms && (kitchen.total < 1 || !kitchen.total)) {
        notify("Please enter a valid Kitchen total amount!", "warning");
        return;
      }
      if (hasClosetForms && !closet.depositOption) {
        notify("Please select a Closet deposit option!", "warning");
        return;
      }
      if (hasClosetForms && !closet.paymentMethod) {
        notify("Please select a Closet payment method!", "warning");
        return;
      }
      if (hasKitchenForms && !kitchen.paymentMethod) {
        notify("Please select a Kitchen payment method!", "warning");
        return;
      }
      if (hasClosetForms && closet.paymentMethod === "Cheque" && closet.chequeNumber.trim() === "") {
        notify("Please enter the Closet cheque number!", "warning");
        return;
      }
      if (hasKitchenForms && kitchen.paymentMethod === "Cheque" && kitchen.chequeNumber.trim() === "") {
        notify("Please enter the Kitchen cheque number!", "warning");
        return;
      }

      const jsonContent = parentClosetsForm;
      jsonContent["designSheetURL"] = designFileURL;
      jsonContent["pricePresentationURL"] = pricePresentationFileURL;
      jsonContent["jobId"] = jobID;

      const finance = {};
      const paymentDetail = {};

      if (hasClosetForms) {
        finance.closet = {
          statuscard: statusCard,
          statuscardnumber: statusCardNumber,
          subTotal: closet.subtotal,
          hst: closet.hst,
          total: closet.total,
        };
        paymentDetail.closet = {
          depositePer: closet.depositOption,
          deposite: closet.deposit,
          balanceDue: closet.balanceDue,
          method: closet.paymentMethod,
          chequeNumber: closet.chequeNumber,
        };
      }

      if (hasKitchenForms) {
        finance.kitchen = {
          statuscard: statusCard,
          statuscardnumber: statusCardNumber,
          subTotal: kitchen.subtotal,
          hst: kitchen.hst,
          total: kitchen.total,
        };
        paymentDetail.kitchen = {
          deposite: kitchen.deposit,
          progressPayment: kitchen.progressPayment,
          balanceDue: kitchen.balanceDue,
          method: kitchen.paymentMethod,
          chequeNumber: kitchen.chequeNumber,
        };
      }

      jsonContent["finance"] = finance;
      jsonContent["paymentDetail"] = paymentDetail;

      const parentResult = await GeneratePDFService.UpdateParentContent(jsonContent);
      if (parentResult) {
        notify("Successfully updated payment details!", "success");
      }
    } catch (err) {
      notify("Unable to save payment details. Please try again.", "error");
    }
  };

  return {
    closet,
    kitchen,
    statusCard, setStatusCard,
    statusCardNumber, setStatusCardNumber,
    paymentDetailsAdded, setPaymentDetailsAdded,
    handlePaymentDetails,
  };
}